import * as Sentry from '@sentry/react';
import { UserIdentifierType } from 'common/analytics-event-manager/IAnalyticsEventManager';
import { ExperimentNames } from 'common/experiments/ExperimentDefinitions';
import { IExperiment } from 'common/experiments/IExperimentManager';
import { isActiveSplit } from 'common/experiments/isActiveSplit';
import { IGatedRollout } from 'common/gated-rollout/GatedRollout';
import { fromPairs } from 'lodash';
import { useEffect } from 'react';
import { setLogRocketExperiments } from 'src/tracking/logRocket';
import { LocalExperimentContext } from './ExperimentContext';

interface UseExperimentReportersParams {
  expContext: LocalExperimentContext;
  variantOverrides: any;
  userId: string | undefined;
  deviceId: string;
  gatedRollout: IGatedRollout;
}
export const useExperimentReporters = ({
  expContext,
  variantOverrides,
  userId,
  deviceId,
  gatedRollout,
}: UseExperimentReportersParams) => {
  useEffect(() => {
    const reportableExperiments = expContext
      .getAllExperiments()
      // filter out user based experiments is user is not signed in
      .filter(({ userIdentifierType }) => userId || userIdentifierType !== UserIdentifierType.USER_ID);
    const toStatusPair = ({ name }: Readonly<IExperiment>): [string, string] => {
      const expName = name as ExperimentNames;
      const variant = expContext.getVariant(expName, false);
      const variantName = variant?.name ?? 'null';
      return [expName, variantName];
    };
    const experimentStatusPairs = reportableExperiments.map(toStatusPair);

    // sentry - sync as tags
    experimentStatusPairs.forEach(([expName, variantName]) => {
      Sentry.setTag(`exp.${expName}`, variantName);
    });

    // logrocket - active splits only (trait payload cap); re-identifies on state transitions
    setLogRocketExperiments(fromPairs(reportableExperiments.filter(isActiveSplit).map(toStatusPair)));
  }, [variantOverrides, expContext, userId, gatedRollout, deviceId]);
};
